import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { getCustomers } from "@api";
import { buildApiQuery } from "@utils";

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

export function useAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [priorityAccounts, setPriorityAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMoreData, setHasMoreData] = useState(false);
  // Full dataset count — only updated from unfiltered fetches
  const [totalRows, setTotalRows] = useState(0);
  // Total matching accounts when a search is active (null when no search)
  const [searchResultCount, setSearchResultCount] = useState(null);

  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const initializedRef = useRef(false);
  const lastSearchRef = useRef("");

  // Merge priority accounts (e.g. existing group members) to the top,
  // deduplicating against paginated results. When searching, only include
  // priority accounts that match the search term.
  const filteredAccounts = useMemo(() => {
    if (!priorityAccounts.length) return accounts;

    let visiblePriority = priorityAccounts;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      visiblePriority = priorityAccounts.filter(
        (a) =>
          a.name?.toLowerCase().includes(term) ||
          a.accountCode?.toLowerCase().includes(term),
      );
    }

    const priorityIds = new Set(visiblePriority.map((a) => a.id));
    const rest = accounts.filter((a) => !priorityIds.has(a.id));
    return [...visiblePriority, ...rest];
  }, [accounts, priorityAccounts, searchTerm]);

  const fetchPage = useCallback(async (page, search, append = false) => {
    const query = buildApiQuery({
      page,
      pageSize: PAGE_SIZE,
      searchTerm: search || undefined,
      sort: "name",
      sortDirection: "asc",
    });

    if (page === 1) setLoading(true);

    try {
      const response = await getCustomers(query);
      const data = response?.data || [];
      const total = response?.rows || 0;

      pageRef.current = page;

      if (append) {
        setAccounts((prev) => [...prev, ...data]);
      } else {
        setAccounts(data);
      }

      if (!search) {
        setTotalRows(total);
      }
      setSearchResultCount(search ? total : null);
      setHasMoreData(page * PAGE_SIZE < total);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const fetchAccounts = () => {
    initializedRef.current = true;
    lastSearchRef.current = "";
    setSearchTerm("");
    setSearchResultCount(null);
    return fetchPage(1, "");
  };

  const fetchMore = () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    fetchPage(pageRef.current + 1, lastSearchRef.current, true);
  };

  // Debounced server-side search — resets to page 1 on every new term
  useEffect(() => {
    if (!initializedRef.current) return;
    if (searchTerm === lastSearchRef.current) return;

    const timer = setTimeout(() => {
      lastSearchRef.current = searchTerm;
      const trimmed = searchTerm.trim();
      if (!trimmed) {
        setSearchResultCount(null);
      }
      fetchPage(1, trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm, fetchPage]);

  const fetchGroupMembers = async (groupId) => {
    if (!groupId) return [];
    const groupQuery = buildApiQuery({ includeGroups: [groupId] });
    const response = await getCustomers(groupQuery);
    return response?.data || [];
  };

  const fetchAllSearchResults = useCallback(async () => {
    const search = lastSearchRef.current?.trim();
    if (!search) return null;

    const resp = await getCustomers(
      buildApiQuery({
        page: 1,
        pageSize: 10000,
        searchTerm: search,
        sort: "name",
        sortDirection: "asc",
      }),
    );
    return resp?.data || [];
  }, []);

  const resetSearch = useCallback(() => {
    setSearchTerm("");
    setAccounts([]);
    setPriorityAccounts([]);
    setHasMoreData(false);
    setTotalRows(0);
    setSearchResultCount(null);
    pageRef.current = 1;
    initializedRef.current = false;
    lastSearchRef.current = "";
  }, []);

  return {
    accounts,
    loading,
    searchTerm,
    setSearchTerm,
    filteredAccounts,
    totalRows,
    searchResultCount,
    hasMoreData,
    fetchAccounts,
    fetchMore,
    fetchGroupMembers,
    fetchAllSearchResults,
    setPriorityAccounts,
    resetSearch,
  };
}
